import type { PipelineArtifact } from "../builder.js";

/**
 * Live ledger of what the pipeline is actually producing — tool names,
 * scanner findings, attack attempts + verdicts. Streams in as the pipeline
 * runs; outsider review #3 ("make the security story visible, not a fact
 * label") is why this exists. Each row is the *real* artifact emitted by
 * @keystone/* packages, not a label.
 */
export function ArtifactLedger(props: { artifacts: PipelineArtifact[] }) {
  if (props.artifacts.length === 0) {
    return (
      <p className="dim" style={{ fontSize: 12, fontFamily: "monospace", textAlign: "center", marginTop: 8 }}>
        waiting for first artifact…
      </p>
    );
  }
  return (
    <div
      style={{
        marginTop: 12,
        background: "#0a0a0a",
        border: "1px solid #1f1f1f",
        borderRadius: 4,
        padding: 8,
        maxHeight: 220,
        overflowY: "auto",
        fontFamily: "monospace",
        fontSize: 12
      }}
    >
      {props.artifacts.map((a, i) => (
        <Row key={i} artifact={a} />
      ))}
    </div>
  );
}

function Row({ artifact }: { artifact: PipelineArtifact }): preact.JSX.Element {
  if (artifact.kind === "tool") {
    return (
      <div style={{ padding: "2px 6px", color: "#aaa", borderLeft: "2px solid " + (artifact.destructive ? "#e57a7a" : artifact.readOnly ? "#7ad48c" : "#888"), marginBottom: 2 }}>
        <span style={{ color: artifact.destructive ? "#e57a7a" : artifact.readOnly ? "#7ad48c" : "#bbb" }}>
          {artifact.destructive ? "✗" : artifact.readOnly ? "○" : "·"}
        </span>{" "}
        <span style={{ color: "#ccc" }}>tool</span> <span style={{ color: "#eee" }}>{artifact.name}</span>
        {artifact.destructive && <span style={{ color: "#e57a7a", marginLeft: 8 }}>destructive</span>}
        {artifact.readOnly && !artifact.destructive && <span style={{ color: "#7ad48c", marginLeft: 8 }}>read-only</span>}
      </div>
    );
  }
  if (artifact.kind === "finding") {
    const sev = artifact.severity.toUpperCase();
    const sevColor = sev === "CRITICAL" || sev === "HIGH" ? "#e57a7a" : sev === "MEDIUM" ? "#e9c46a" : "#888";
    return (
      <div style={{ padding: "2px 6px", color: "#aaa", borderLeft: "2px solid " + sevColor, marginBottom: 2 }}>
        <span style={{ color: sevColor, fontWeight: "bold" }}>{sev}</span>{" "}
        <span style={{ color: "#999" }}>{artifact.ruleId}</span>{" "}
        <span style={{ color: "#eee" }}>{artifact.toolName}</span>
        <div style={{ color: "#888", paddingLeft: 18, fontSize: 11 }}>{artifact.rationale}</div>
      </div>
    );
  }
  // attack
  const blocked = artifact.verdict === "blocked";
  return (
    <div style={{ padding: "2px 6px", color: "#aaa", borderLeft: "2px solid " + (blocked ? "#7ad48c" : "#e57a7a"), marginBottom: 2 }}>
      <span style={{ color: blocked ? "#7ad48c" : "#e57a7a", fontWeight: "bold" }}>
        {blocked ? "BLOCKED" : "EXPLOITED"}
      </span>{" "}
      <span style={{ color: "#999" }}>{artifact.id}</span>
      <div style={{ color: "#888", paddingLeft: 8, fontSize: 11, textDecoration: blocked ? "line-through" : "none" }}>
        {artifact.description}
      </div>
    </div>
  );
}
